import { skipToken, useQuery } from '@tanstack/react-query';
import {
  getV1CompaniesCompanyIdMatchKeys,
  MatchKey_Read,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useMemo } from 'react';

import { MatchKey } from '~hooks/useMatchKeys/models';
import connection from '~services/connectionModule';
import { fetchAllApiPages } from '~utils/fetchAllPages';

import { RARELY_CHANGED_DATA_STALE_TIME_IN_MS } from './constants';

// Root key: a mutation invalidates this prefix to reach every company and matchable type.
export const COMPANY_MATCH_KEYS_QUERY_KEY = 'companyMatchKeys';

interface UseCompanyMatchKeysQueryProps {
  companyId?: string;
  isEnabled: boolean;
  matchableType: MatchKey_Read['matchable_type'];
}

const groupByMatchableId = (matchKeys: MatchKey[]) => {
  const grouped = new Map<string, MatchKey[]>();

  matchKeys.forEach((matchKey) => {
    const recordMatchKeys = grouped.get(matchKey.matchableId);

    if (recordMatchKeys) {
      recordMatchKeys.push(matchKey);
    } else {
      grouped.set(matchKey.matchableId, [matchKey]);
    }
  });

  return grouped;
};

export default function useCompanyMatchKeysQuery({
  companyId,
  isEnabled,
  matchableType,
}: UseCompanyMatchKeysQueryProps) {
  const { data } = useQuery({
    queryKey: [COMPANY_MATCH_KEYS_QUERY_KEY, companyId, matchableType],
    // Not useMatchKeys: its fetch returns [] on failure, which reads as "no match keys".
    queryFn:
      isEnabled && companyId
        ? async ({ signal }) => {
            try {
              return await fetchAllApiPages(
                (query, pageSignal) =>
                  getV1CompaniesCompanyIdMatchKeys({
                    path: { 'company-id': companyId },
                    query: { ...query, matchable_type: matchableType },
                    signal: pageSignal,
                  }),
                MatchKey.parse,
                { signal },
              );
            } catch (error) {
              if (signal.aborted) {
                throw error;
              }

              connection.handleRequestError(
                error,
                t('error_messages.match_keys.failed_to_fetch_match_keys'),
              );
            }
          }
        : skipToken,
    // handleRequestError already toasts and reports, so React Query's default of three
    // retries would surface one failure four times.
    retry: false,
    // The query walks every match key of one type for the whole company, and match keys
    // only change through this app — where the mutation invalidation refreshes the grid.
    staleTime: RARELY_CHANGED_DATA_STALE_TIME_IN_MS,
  });

  const matchKeysByMatchableId = useMemo(
    () => (data ? groupByMatchableId(data) : null),
    [data],
  );

  return { matchKeysByMatchableId };
}
